import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../state/useAuth';
import { useData } from '../../../context/DataContext';
import { Clock, MapPin, ChevronRight, Calendar as CalIcon, ChevronLeft, FileText } from 'lucide-react';
import CompactTravelIndicator from '../../../components/worker/CompactTravelIndicator';

const today = new Date();
today.setHours(0,0,0,0);
const todayStr = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

const VIEW = { DAY: 'day', WEEK: 'week', MONTH: 'month' };

// --- Mini Calendar Component (Month) ---
const MiniCalendar = ({ selectedDate, onSelect, assignments, businessHours }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  
  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const startDay = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return d === 0 ? 6 : d - 1; // Adjust to Monday start
  };

  const monthName = currentMonth.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
  const days = [];
  const totalDays = daysInMonth(currentMonth);
  const firstDay = startDay(currentMonth);

  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= totalDays; i++) days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));

  const hasAssignment = (date) => {
    if (!date || !assignments) return false;
    const d = new Date(date); d.setHours(0,0,0,0);
    return assignments.some(a => {
      const s = new Date(a.start_date); s.setHours(0,0,0,0);
      const e = new Date(a.end_date || a.start_date); e.setHours(0,0,0,0);
      return d >= s && d <= e;
    });
  };

  const isClosed = (date) => {
    if (!date || !businessHours) return false;
    const day = date.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
    return businessHours[day] && !businessHours[day].active;
  };

  return (
    <div className="mini-cal scale-in">
      <div className="cal-header">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}><ChevronLeft size={18} /></button>
        <span className="month-label">{monthName}</span>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}><ChevronRight size={18} /></button>
      </div>
      <div className="cal-grid">
        {['L','M','M','J','V','S','D'].map(d => <div key={d} className="day-name">{d}</div>)}
        {days.map((d, i) => {
          if (!d) return <div key={`p-${i}`} className="day-empty" />;
          const isSelected = d.toDateString() === selectedDate.toDateString();
          const hasAs = hasAssignment(d);
          const isToday = d.toDateString() === today.toDateString();
          const closed = isClosed(d);
          return (
            <div key={i} className={`day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${closed ? 'is-closed' : ''}`} onClick={() => onSelect(d)}>
              {d.getDate()}
              {hasAs && <div className="dot" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Week Row Component ---
const WeekCalendar = ({ selectedDate, onSelect, assignments, businessHours }) => {
  const weekDays = useMemo(() => {
    const start = new Date(selectedDate);
    start.setDate(selectedDate.getDate() - (selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [selectedDate]);

  const rangeStr = useMemo(() => {
    const start = weekDays[0];
    const end = weekDays[6];
    const sStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const eStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${sStr} - ${eStr}`;
  }, [weekDays]);

  const hasAssignment = (date) => {
    if (!date || !assignments) return false;
    const d = new Date(date); d.setHours(0,0,0,0);
    return assignments.some(a => {
      const s = new Date(a.start_date); s.setHours(0,0,0,0);
      const e = new Date(a.end_date || a.start_date); e.setHours(0,0,0,0);
      return d >= s && d <= e;
    });
  };

  const isClosed = (date) => {
    if (!date || !businessHours) return false;
    const day = date.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
    return businessHours[day] && !businessHours[day].active;
  };

  const navWeek = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + (dir * 7));
    onSelect(d);
  };

  return (
    <div className="week-nav-container scale-in">
      <div className="week-header">
        <button onClick={() => navWeek(-1)} className="week-nav-btn"><ChevronLeft size={18} /></button>
        <span className="week-range-label">{rangeStr}</span>
        <button onClick={() => navWeek(1)} className="week-nav-btn"><ChevronRight size={18} /></button>
      </div>
      <div className="week-row-cal">
        {weekDays.map((d, i) => {
          const isSelected = d.toDateString() === selectedDate.toDateString();
          const isToday = d.toDateString() === today.toDateString();
          const hasAs = hasAssignment(d);
          const closed = isClosed(d);
          const dayName = d.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0).toUpperCase();
          return (
            <div key={i} className={`week-day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${closed ? 'is-closed' : ''}`} onClick={() => onSelect(d)}>
              <span className="w-day-name">{dayName}</span>
              <span className="w-day-num">{d.getDate()}</span>
              {hasAs && <div className="w-dot" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function WorkerPlanning({ onOpenChantier }) {
  const { model: user } = useAuth();
  const { assignments, settings, loading: globalLoading } = useData();
  const [view, setView] = useState(VIEW.DAY);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const myAssignments = useMemo(() => {
    if (!user || !assignments) return [];
    return assignments.filter(a => {
      // Check if user is an array (multi-select) or a single ID
      const u = a.user;
      if (Array.isArray(u)) {
        return u.some(id => (typeof id === 'string' ? id : id.id) === user.id);
      }
      const aUserId = typeof u === 'string' ? u : u?.id;
      return aUserId === user.id;
    });
  }, [user, assignments]);

  const filtered = useMemo(() => {
    // Filter by VIEW and selectedDate
    const s = new Date(selectedDate); s.setHours(0,0,0,0);
    const t = new Date(today); t.setHours(0,0,0,0);

    const initialFilter = myAssignments.filter(a => {
      const start = new Date(a.start_date); start.setHours(0,0,0,0);
      const end = new Date(a.end_date || a.start_date); end.setHours(0,0,0,0);
      
      const targetDate = (view === VIEW.DAY) ? t : s;
      return targetDate >= start && targetDate <= end;
    });

    // Grouping to avoid duplicates (Deduplication)
    const groups = {};
    initialFilter.forEach(a => {
      const key = (a.type === 'rdv') 
        ? `rdv-${a.title}-${a.start_date}-${a.end_date}` 
        : `mission-${a.chantier}-${a.start_date}-${a.end_date}`;
      
      if (!groups[key]) groups[key] = a;
    });

    return Object.values(groups).sort((a,b) => {
      // 1. Priority to RDVs
      if (a.type === 'rdv' && b.type !== 'rdv') return -1;
      if (a.type !== 'rdv' && b.type === 'rdv') return 1;

      // 2. Secondary sort by date
      return new Date(a.start_date) - new Date(b.start_date);
    });
  }, [myAssignments, view, selectedDate]);

  return (
    <div className="worker-planning-page" style={{ paddingBottom: 100 }}>
      {/* Header */}
      <div className="w-page-header">
        <div>
          <h1 className="w-page-title">Mon Planning</h1>
          <p className="w-page-subtitle">
            {view === VIEW.DAY ? todayStr : selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="w-view-switch">
          {[VIEW.DAY, VIEW.WEEK, VIEW.MONTH].map(v => (
            <button key={v} className={view === v ? 'active' : ''} onClick={() => { setView(v); if(v === VIEW.DAY) setSelectedDate(new Date()); }}>
              {v === VIEW.DAY ? 'Jour' : v === VIEW.WEEK ? 'Sem.' : 'Mois'}
            </button>
          ))}
        </div>
      </div>

      <div className="w-content-area">
        {/* Render Week Calendar */}
        {view === VIEW.WEEK && (
          <WeekCalendar selectedDate={selectedDate} onSelect={setSelectedDate} assignments={myAssignments} businessHours={settings?.business_hours} />
        )}

        {/* Render Month Calendar */}
        {view === VIEW.MONTH && (
          <MiniCalendar selectedDate={selectedDate} onSelect={setSelectedDate} assignments={myAssignments} businessHours={settings?.business_hours} />
        )}

        {/* Open/Closed indicator */}
        {(() => {
          const dayKey = selectedDate.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
          const cfg = settings?.business_hours?.[dayKey];
          if (cfg && !cfg.active) return (
            <div className="w-closed-banner scale-in">
              <Clock size={16} />
              <span>Entreprise fermée ce jour</span>
            </div>
          );
          if (cfg && cfg.active) return (
            <div className="w-open-badge scale-in">
              <Clock size={14} />
              <span>Horaires bureau : {cfg.open} — {cfg.close}</span>
            </div>
          );
          return null;
        })()}

        {globalLoading ? (
          <div className="w-skeleton-stack">
            {[1, 2].map(i => <div key={i} className="w-skeleton-card pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="w-empty-state scale-in">
            <div className="empty-icon"><CalIcon size={48} /></div>
            <h3>Aucune mission</h3>
            <p>Rien de prévu pour cette date. {view === VIEW.DAY ? "Profitez-en !" : ""}</p>
          </div>
        ) : (
          filtered.map(a => {
            const isRDV = a.type === 'rdv';
            const ch = a.expand?.chantier || { title: isRDV ? (a.title || 'Rendez-vous') : 'Chantier Sans Nom', color: isRDV ? '#a855f7' : '#3b82f6' };
            const isUrgent = !isRDV && ch.priority === 'urgent';
            const displayTitle = isRDV ? (a.title || 'Rendez-vous') : ch.title;
            const displayColor = isRDV ? '#a855f7' : (ch.color || '#3b82f6');

            return (
              <div key={a.id} className="w-card-wrapper scale-in">
                <div className="w-card premium" 
                  onClick={() => !isRDV && onOpenChantier({ 
                    ...ch, 
                    assignmentNotes: a.notes, 
                    assignmentId: a.id,
                    assignmentStart: a.start_date,
                    assignmentEnd: a.end_date
                  })}
                  style={{ borderLeft: `8px solid ${displayColor}`, cursor: isRDV ? 'default' : 'pointer' }}
                >
                  <div className="w-card-top">
                    <div className="w-card-main-info">
                      <h3 style={{ color: displayColor }}>{displayTitle}</h3>
                      <p className="client-name">{isRDV ? 'Rendez-vous AEF 32' : (ch.client_name || 'Client AEF 32')}</p>
                    </div>
                    <div className="w-badge-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <div className={`w-badge ${isRDV ? 'rdv' : (isUrgent ? 'urgent' : ch.status)}`} style={isRDV ? { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' } : {}}>
                        {isRDV ? 'RENDEZ-VOUS' : (isUrgent ? 'URGENT' : ch.status === 'completed' ? 'TERMINÉ' : 'À PLANIFIER')}
                      </div>
                      {!isRDV && (() => {
                        const now = new Date();
                        const s = new Date(a.start_date);
                        const e = new Date(a.end_date || a.start_date);
                        if (now >= s && now <= e) return <div className="w-live-badge active">EN COURS 🟢</div>;
                        if (now < s) {
                          const diff = Math.round((s - now) / (1000 * 60));
                          if (diff < 60) return <div className="w-live-badge future">DANS {diff} MIN ⏳</div>;
                          return <div className="w-live-badge future">À VENIR 🕒</div>;
                        }
                        return <div className="w-live-badge past">PASSÉ ✓</div>;
                      })()}
                    </div>
                  </div>

                  <div className="w-card-rows">
                    <div className="w-label-row">
                      <Clock size={14} />
                      <span style={{ fontSize: 13, fontWeight: 900, color: '#0f172a' }}>
                        {(() => {
                           const s = new Date(a.start_date);
                           const e = new Date(a.end_date || a.start_date);
                           const d = new Date(selectedDate);
                           const dayKey = d.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
                           const cfg = settings?.business_hours?.[dayKey] || { open: '08:00', close: '17:00' };

                           const isStartDay = d.toDateString() === s.toDateString();
                           const isEndDay = d.toDateString() === e.toDateString();
                           
                           let dispStart = cfg.open;
                           let dispEnd = cfg.close;

                           if (isStartDay) dispStart = s.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                           if (isEndDay) dispEnd = e.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                           
                           return `${dispStart} — ${dispEnd}`;
                        })()}
                      </span>
                    </div>
                    {a.notes && (
                      <div className="w-label-row" style={{ color: '#f59e0b', background: '#fffbeb', padding: '4px 8px', borderRadius: 8, marginTop: 4 }}>
                        <FileText size={12} />
                        <span style={{ fontSize: 10, fontWeight: 800 }}>Notes : {a.notes}</span>
                      </div>
                    )}
                    {!isRDV && (
                      <div className="w-label-row">
                        <MapPin size={14} />
                        <span className="truncate">{ch.location || 'Localisation non définie'}</span>
                      </div>
                    )}
                  </div>

                  {!isRDV && (
                    <>
                      <div className="w-nav-actions">
                        <button 
                          onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ch.location || '')}`, '_blank'); }}
                          className="w-nav-btn maps"
                        >
                          <img src="https://www.gstatic.com/images/branding/product/2x/maps_96dp.png" alt="Maps" />
                          Maps
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); window.open(`https://waze.com/ul?q=${encodeURIComponent(ch.location || '')}&navigate=yes`, '_blank'); }}
                          className="w-nav-btn waze"
                        >
                          <img src="https://upload.wikimedia.org/wikipedia/commons/e/e0/Waze_icon.png" alt="Waze" />
                          Waze
                        </button>
                      </div>

                      {ch.lat && settings?.base_lat && (
                        <CompactTravelIndicator lat1={settings.base_lat} lon1={settings.base_lon} lat2={ch.lat} lon2={ch.lon} />
                      )}

                      <div className="w-card-footer">
                        <div className="tap-hint">Consulter les détails</div>
                        <ChevronRight size={18} className="arrow" />
                      </div>
                    </>
                  )}
                  
                  {isRDV && (
                    <div className="w-card-footer" style={{ borderTop: 'none', padding: '8px 18px 15px' }}>
                       <div className="tap-hint" style={{ color: '#a855f7' }}>Rendez-vous Administratif</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        .worker-planning-page { min-height: 100vh; background: #f8fafc; }
        .w-page-header { padding: 30px 20px 15px; display: flex; justify-content: space-between; align-items: flex-end; }
        .w-page-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0; }
        .w-page-subtitle { font-size: 13px; font-weight: 700; color: #94a3b8; margin: 4px 0 0; text-transform: capitalize; }
        
        .w-view-switch { background: #e2e8f0; padding: 4px; border-radius: 12px; display: flex; gap: 4px; }
        .w-view-switch button { border: none; background: none; padding: 6px 12px; font-size: 10px; font-weight: 900; border-radius: 8px; cursor: pointer; color: #64748b; transition: all 0.2s; }
        .w-view-switch button.active { background: #fff; color: #3b82f6; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }

        /* Week Row Calendar */
        .week-nav-container { background: #fff; border-radius: 28px; padding: 15px; margin-bottom: 15px; border: 1px solid #f1f5f9; box-shadow: 0 4px 12px -2px rgba(0,0,0,0.02); }
        .week-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; padding: 0 5px; }
        .week-range-label { font-size: 13px; font-weight: 900; color: #0f172a; text-transform: lowercase; }
        .week-nav-btn { border: none; background: #f8fafc; width: 32px; height: 32px; border-radius: 10px; display: flex; align-items: center; justifyContent: center; cursor: pointer; color: #64748b; }
        .week-row-cal { display: flex; justify-content: space-between; gap: 4px; }
        
        .week-day-cell { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px 0; border-radius: 12px; cursor: pointer; position: relative; transition: all 0.2s; }
        .week-day-cell.selected { background: #3b82f6; color: #fff; }
        .w-day-name { font-size: 10px; font-weight: 900; color: #94a3b8; opacity: 0.8; }
        .selected .w-day-name { color: rgba(255,255,255,0.7); }
        .w-day-num { font-size: 14px; font-weight: 900; }
        .w-dot { width: 4px; height: 4px; background: #3b82f6; border-radius: 50%; position: absolute; bottom: 4px; }
        .selected .w-dot { background: #fff; }
        .week-day-cell.today { border: 1.5px solid #3b82f6; }
        .is-closed { opacity: 0.35; background: #f1f5f9 !important; pointer-events: none; }
        .selected.is-closed { background: #cbd5e1 !important; color: #475569 !important; }

        /* Closed Banner */
        .w-closed-banner { background: #fff1f2; border: 1px solid #fecaca; padding: 12px; border-radius: 16px; display: flex; align-items: center; justifyContent: center; gap: 10px; color: #e11d48; font-size: 13px; font-weight: 800; margin-bottom: 10px; }
        .w-open-badge { background: #f8fafc; border: 1.5px solid #e2e8f0; padding: 8px 12px; border-radius: 12px; display: flex; align-items: center; justifyContent: center; gap: 8px; color: #64748b; font-size: 11px; font-weight: 900; margin-bottom: 15px; }

        /* Mini Calendar */
        .mini-cal { background: #fff; border-radius: 24px; padding: 20px; margin-bottom: 15px; border: 1px solid #f1f5f9; box-shadow: 0 4px 10px -2px rgba(0,0,0,0.02); }
        .cal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
        .month-label { font-size: 13px; font-weight: 900; color: #0f172a; text-transform: capitalize; }
        .cal-header button { border: none; background: #f8fafc; width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justifyContent: center; cursor: pointer; color: #64748b; }
        .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
        .day-name { text-align: center; font-size: 10px; font-weight: 900; color: #cbd5e1; padding-bottom: 5px; }
        .day-cell { height: 36px; display: flex; flex-direction: column; align-items: center; justifyContent: center; font-size: 12px; font-weight: 800; color: #475569; position: relative; border-radius: 10px; cursor: pointer; }
        .day-cell.selected { background: #3b82f6; color: #fff; }
        .day-cell.today { border: 1.5px solid #3b82f6; color: #3b82f6; }
        .day-cell.today.selected { color: #fff; }
        .dot { width: 3px; height: 3px; background: #3b82f6; border-radius: 50%; position: absolute; bottom: 4px; }
        .day-cell.selected .dot { background: #fff; }

        .w-content-area { padding: 0 16px; display: flex; flex-direction: column; gap: 12px; }
        .w-skeleton-card { height: 160px; background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; }
        .pulse { animation: pulse 1.5s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

        .w-card.premium { background: #fff; border-radius: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); overflow: hidden; display: flex; flex-direction: column; }
        .w-card-top { padding: 15px 18px 10px; display: flex; justify-content: space-between; align-items: flex-start; }
        .w-card-main-info h3 { margin: 0; font-size: 17px; font-weight: 900; line-height: 1.2; }
        .client-name { margin: 2px 0 0; font-size: 12px; font-weight: 700; color: #94a3b8; }
        .w-badge { font-size: 8px; font-weight: 900; padding: 3px 6px; border-radius: 6px; background: #f1f5f9; color: #64748b; }
        .w-badge.urgent { background: #fee2e2; color: #ef4444; }
        
        .w-live-badge { font-size: 8px; font-weight: 900; padding: 2px 6px; border-radius: 4px; letter-spacing: 0.5px; }
        .w-live-badge.active { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .w-live-badge.future { background: #eff6ff; color: #1e40af; border: 1px solid #dbeafe; }
        .w-live-badge.past { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; opacity: 0.7; }

        .w-card-rows { padding: 0 18px 12px; display: flex; flex-direction: column; gap: 6px; }
        .w-label-row { display: flex; align-items: center; gap: 6px; color: #64748b; }
        .w-label-row span { font-size: 11px; font-weight: 700; }
        .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 200px; }
        
        .w-nav-actions { padding: 0 18px 15px; display: flex; gap: 8px; }
        .w-nav-btn { flex: 1; display: flex; align-items: center; justifyContent: center; gap: 6px; padding: 10px; border-radius: 12px; border: none; font-size: 11px; font-weight: 900; color: #fff; cursor: pointer; transition: transform 0.2s; }
        .w-nav-btn:active { transform: scale(0.95); }
        .w-nav-btn.maps { background: #4285F4; }
        .w-nav-btn.waze { background: #33ccff; }
        .w-nav-btn img { width: 14px; height: 14px; }

        .w-card-footer { padding: 10px 18px; border-top: 1px solid #f8fafc; display: flex; justify-content: space-between; align-items: center; background: #fcfdfe; }
        .tap-hint { font-size: 10px; font-weight: 800; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.5px; }
        .arrow { color: #cbd5e1; }

        .scale-in { animation: scaleIn 0.3s ease-out; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}
